import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, api } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { tr, enUS, nl } from 'date-fns/locale';
import { 
  Plus, 
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Eye,
  Trash2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { cn } from '../lib/utils';

const statusConfig = {
  PENDING: { icon: Clock, color: 'bg-amber-50 text-amber-700 border-amber-200', label: 'pending' },
  APPROVED: { icon: CheckCircle, color: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'approved' },
  REJECTED: { icon: XCircle, color: 'bg-red-50 text-red-700 border-red-200', label: 'rejected' }
};

const getDateLocale = (lang) => {
  switch (lang) {
    case 'tr': return tr;
    case 'nl': return nl;
    default: return enUS;
  }
};

export const LeaveRequests = () => {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  
  // Dialog states
  const [viewDialog, setViewDialog] = useState({ open: false, request: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, request: null });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchYears();
    fetchRequests();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, activeTab]);

  const fetchYears = async () => {
    try {
      const response = await api.get('/me/balances/years');
      if (response.data.success) {
        setAvailableYears(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch years', error);
    }
  };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      let url = `/leave/requests?year=${selectedYear}`;
      if (activeTab !== 'all') {
        url += `&status=${activeTab.toUpperCase()}`;
      }
      const response = await api.get(url);
      if (response.data.success) {
        setRequests(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch leave requests');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.request) return;
    setProcessing(true);
    
    try {
      await api.delete(`/leave/requests/${deleteDialog.request.id}`);
      toast.success(t('leave.requestDeleted') || 'İzin talebi silindi');
      setDeleteDialog({ open: false, request: null });
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setProcessing(false);
    }
  };

  const StatusBadge = ({ status }) => {
    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;
    return (
      <Badge variant="outline" className={cn("gap-1", config.color)}>
        <Icon className="h-3 w-3" />
        {t(`leave.${config.label}`)}
      </Badge>
    );
  };

  const RequestCard = ({ request }) => {
    const startDate = new Date(request.start_date);
    const endDate = new Date(request.end_date);
    const dateLocale = getDateLocale(language);
    const hasSelectedDays = request.selected_days && request.selected_days.length > 0;
    
    // Format selected days for display
    const formatSelectedDays = (days) => {
      if (!days || days.length === 0) return null;
      if (days.length <= 3) {
        return days.map(d => format(new Date(d), 'd MMM', { locale: dateLocale })).join(', ');
      }
      const first3 = days.slice(0, 3).map(d => format(new Date(d), 'd MMM', { locale: dateLocale })).join(', ');
      return `${first3} +${days.length - 3}`;
    };
    
    return (
      <div 
        className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl p-4 hover:shadow-md transition-shadow"
        data-testid={`request-card-${request.id}`}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-slate-100">{request.leave_type_name}</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">{request.days_count} {t('leave.days')}</p>
          </div>
          <StatusBadge status={request.status} />
        </div>
        
        <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 mb-3">
          <Calendar className="h-4 w-4" />
          <span>
            {hasSelectedDays 
              ? formatSelectedDays(request.selected_days)
              : `${format(startDate, 'PP', { locale: dateLocale })} - ${format(endDate, 'PP', { locale: dateLocale })}`
            }
          </span>
        </div>
        
        {request.description && (
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-3 line-clamp-2">{request.description}</p>
        )}
        
        <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-700">
          <span className="text-xs text-slate-400">
            {formatDate(request.created_at)}
          </span>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setViewDialog({ open: true, request })}
            >
              <Eye className="h-4 w-4" />
            </Button>
            {request.status === 'PENDING' && (
              <Button 
                variant="ghost" 
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => setDeleteDialog({ open: true, request })}
                data-testid={`delete-btn-${request.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const filteredRequests = activeTab === 'all' 
    ? requests 
    : requests.filter(r => r.status === activeTab.toUpperCase());

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leave-requests-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 
            className="text-2xl font-bold text-slate-900 dark:text-slate-100"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            {t('leave.myRequests')}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">{t('leave.requests')}</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Year Selector */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setSelectedYear(selectedYear - 1)}
              disabled={selectedYear <= 2020}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(availableYears.length > 0 ? availableYears : [selectedYear]).map(year => (
                  <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setSelectedYear(selectedYear + 1)}
              disabled={selectedYear >= new Date().getFullYear() + 1}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Create Button */}
          <Button 
            onClick={() => navigate('/dashboard/leave-requests/create')}
            className="gap-2"
            data-testid="create-request-btn"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">{t('leave.newRequest')}</span>
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full lg:w-auto">
          <TabsTrigger value="all" data-testid="tab-all">
            {t('common.all')}
          </TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending">
            {t('leave.pending')}
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">
            {t('leave.approved')}
          </TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">
            {t('leave.rejected')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl p-12 text-center">
              <Calendar className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">{t('leave.noRequests')}</h3>
              <p className="text-slate-500 dark:text-slate-400 mb-6">Create your first leave request to get started</p>
              <Button onClick={() => navigate('/dashboard/leave-requests/create')} className="gap-2">
                <Plus className="h-4 w-4" />
                {t('leave.newRequest')}
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredRequests.map((request) => (
                <RequestCard key={request.id} request={request} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* View Details Dialog */}
      <Dialog open={viewDialog.open} onOpenChange={(open) => setViewDialog({ open, request: viewDialog.request })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('leave.viewDetails')}</DialogTitle>
            <DialogDescription>
              {viewDialog.request?.leave_type_name}
            </DialogDescription>
          </DialogHeader>
          
          {viewDialog.request && (
            <div className="space-y-4 py-4">
              <div className="flex justify-between py-2 border-b">
                <span className="text-slate-500 dark:text-slate-400">Status</span>
                <StatusBadge status={viewDialog.request.status} />
              </div>
              
              {/* Selected Days */}
              {viewDialog.request.selected_days && viewDialog.request.selected_days.length > 0 ? (
                <div className="py-2 border-b">
                  <span className="text-slate-500 dark:text-slate-400 block mb-2">{t('leave.selectedDates') || 'Seçilen Tarihler'}</span>
                  <div className="flex flex-wrap gap-1">
                    {viewDialog.request.selected_days.map((day, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {format(new Date(day), 'd MMM yyyy', { locale: getDateLocale(language) })}
                      </Badge>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-slate-500 dark:text-slate-400">{t('leave.startDate')}</span>
                    <span className="font-medium">{viewDialog.request.start_date}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-slate-500 dark:text-slate-400">{t('leave.endDate')}</span>
                    <span className="font-medium">{viewDialog.request.end_date}</span>
                  </div>
                </>
              )}
              
              <div className="flex justify-between py-2 border-b">
                <span className="text-slate-500 dark:text-slate-400">{t('leave.days')}</span>
                <span className="font-bold text-lg">{viewDialog.request.days_count}</span>
              </div>
              {viewDialog.request.description && (
                <div className="py-2">
                  <span className="text-slate-500 dark:text-slate-400 block mb-1">{t('leave.description')}</span>
                  <p className="text-slate-900 dark:text-slate-100">{viewDialog.request.description}</p>
                </div>
              )}
              {viewDialog.request.admin_note && (
                <div className="py-2 p-3 bg-slate-50 dark:bg-slate-800 dark:bg-slate-900 rounded-lg">
                  <span className="text-slate-500 dark:text-slate-400 block mb-1">Admin Note</span>
                  <p className="text-slate-900 dark:text-slate-100">{viewDialog.request.admin_note}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialog({ open: false, request: null })}>
              {t('common.cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, request: deleteDialog.request })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('leave.deleteRequest') || 'İzin Talebini Sil'}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('leave.deleteRequestConfirm') || 'Bu izin talebini silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={processing}
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('common.delete') || 'Sil'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LeaveRequests;
